import { DateTime } from 'luxon';
import { getMeasurementDelayBucket, getMeasurementDelayBucketColor, NUM_MEASUREMENT_DELAY_BUCKETS } from '../../../../shared/services/measurementDelayBuckets.ts';
import { getShiftCodeAt } from '../../../../shared/services/shifts/shifts.ts';
import { MeasurementDelay } from '../../../../shared/types/measurementDelay.ts';
import { sumOf } from '../../../../shared/utils/arrayUtils.ts';
import { Bar, BarGraphData, BarGroup, BarSection, Label } from '../barGraphData.ts';
import { PeriodOption, ReportSettings } from '../report-settings/reportSettings.ts';


interface MeasurementDelay2 extends MeasurementDelay {
    isOnWeekend: boolean;
}


export function prepareMeasurementDelays(
    measurementDelays: MeasurementDelay[],
    reportSettings: ReportSettings,
): BarGraphData {
    const periods = reportSettings.periodsInRange;
    const splitShifts = reportSettings.shiftsDisplay === 'Separated';

    const transformed = transformInputs(measurementDelays);
    const filtered = filterInitial(transformed, reportSettings);

    const barGroups: BarGroup[] = [];
    for (const period of periods) {
        const barGroup = getBarGroup(
            filtered,
            period,
            splitShifts,
            reportSettings.visibleShifts,
        );
        barGroups.push(barGroup);
    }

    return {
        maxValue: 1,
        barGroups,
    }
}

function transformInputs(measurementDelays: MeasurementDelay[]): MeasurementDelay2[] {
    return measurementDelays.map(it => ({
        ...it,
        isOnWeekend: it.targetShiftCode === 'W',
        targetShiftCode: getShiftCodeAt(it.targetTime!, false),//Override target shift code to convert W shifts to A/B/N
    }));
}

function filterInitial(
    measurementDelays: MeasurementDelay2[],
    reportSettings: ReportSettings,
): MeasurementDelay2[] {
    return measurementDelays
        .filter(it => reportSettings.visibleShifts.includes(it.targetShiftSlot!))
        .filter(it => it.isOnWeekend && reportSettings.weekends || !it.isOnWeekend && reportSettings.workdays);
}

// A bar group correspons to one unit of time. It is either one bar or separated A/B shift bars
function getBarGroup(
    measurementDelays: MeasurementDelay[],
    period: PeriodOption,
    splitShifts: boolean,
    visibleShifts: string[],
): BarGroup {
    const delaysInPeriod = measurementDelays.filter(it =>
        !!it.targetTime && it.targetTime >= period.start && it.targetTime < period.end
    );

    const bars = [];
    if (splitShifts) {
        const groupedDelays = splitABShifts(delaysInPeriod);
        const aShiftBar = getBar(groupedDelays[0], 'A');
        const bShiftBar = getBar(groupedDelays[1], 'B');
        const nShiftBar = getBar(groupedDelays[2], 'N');
        const firstShiftA = isFirstShiftA(period);
        bars.push(firstShiftA ? aShiftBar : bShiftBar);
        bars.push(firstShiftA ? bShiftBar : aShiftBar);
        if (visibleShifts.includes('night')) {
            bars.push(nShiftBar);
        }
    } else {
        const filteredDelays = filterWorkShifts(delaysInPeriod);
        bars.push(getBar(filteredDelays, null));
    }

    return {
        periodLabel: period.periodLabel,
        bars,
    };
}

function isFirstShiftA(period: PeriodOption): boolean {
    const morningShiftTime = DateTime.fromJSDate(period.start).set({ hour: 10, minute: 0, second: 0, millisecond: 0 }); // Middle of morning shift
    const morningShiftCode = getShiftCodeAt(morningShiftTime);
    return morningShiftCode === 'A';
}

function getBar(delays: MeasurementDelay[], shiftLabel: string | null): Bar {
    const bucketCounts = getDelaysHistogram(delays);
    const totalCount = sumOf(bucketCounts, it => it);
    const sections = bucketCounts.map((bucketCount, index) => getBarSegment(bucketCount, totalCount, index));

    const labels: Label[] = shiftLabel
        ? [{ position: 'above-bottom', text: shiftLabel }]
        : [];

    return {
        labels,
        sections,
    }
}

function getBarSegment(
    bucketCount: number,
    totalCount: number,
    bucketIndex: number,
): BarSection {
    const bucketPercentage = bucketCount / totalCount;
    const percentageRounded = Math.round(bucketPercentage * 100);
    const bucketLabel = `${percentageRounded}%`;
    const bucketColor = getMeasurementDelayBucketColor(bucketIndex);
    return {
        value: bucketPercentage,
        labels: [{ position: 'middle', text: bucketLabel }],
        color: bucketColor,
    };
}


function splitABShifts(delays: MeasurementDelay[]): MeasurementDelay[][] {
    const delaysInShiftA = [];
    const delaysInShiftB = [];
    const delaysInShiftN = [];
    for (const measurementDelay of delays) {
        const shiftName = measurementDelay.targetShiftCode;

        if (shiftName === 'A') {
            delaysInShiftA.push(measurementDelay);
        } else if (shiftName === 'B') {
            delaysInShiftB.push(measurementDelay);
        } else if (shiftName === 'N') {
            delaysInShiftN.push(measurementDelay);
        }
    }
    return [delaysInShiftA, delaysInShiftB, delaysInShiftN];
}

function filterWorkShifts(delays: MeasurementDelay[]): MeasurementDelay[] {
    return delays.filter(it => it.targetShiftCode === 'A' || it.targetShiftCode === 'B');
}

function getDelaysHistogram(delays: MeasurementDelay[]): number[] {
    const bucketCounts = new Array(NUM_MEASUREMENT_DELAY_BUCKETS).fill(0);
    for (const measurementDelay of delays) {
        if (measurementDelay.delay == null) continue;
        const bucket = getMeasurementDelayBucket(measurementDelay.delay);
        bucketCounts[bucket]++;
    }
    return bucketCounts;
}
